import { Box, Button, FormControl, Modal, Select, MenuItem } from '@mui/material'
import { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { mainGreen, mainGrey, mainRed } from '../utils/color'
import CustomInput from './CustomInput'
import { apiBase } from '../services/apiBase'
import { fetchStores, selectStores } from '../redux/slices/storeSlice'
import { api } from '../utils/api'
import { compressImage, formatPrice } from '../utils/helper'
import { enqueueSnackbar } from 'notistack'
import CameraAltIcon from '@mui/icons-material/CameraAlt'

const initValues = {
	vani: '',
	cafe: '',
	banhGion: '',
	chaGio: '',
	freshQuantity: '',
	cookedQuantity: '',
	totalPrice: '',
	phiGiaoHang: '',
	thongTinDonHang: '',
	selectedStore: '',
	ghiChu: '',
}

export default function InternalSaleForm({ initOrder, closeForm, open }) {
	const dispatch = useDispatch()

	const storesFromRedux = useSelector(selectStores)

	const [orderData, setOrderData] = useState(initValues)
	const [isSubmitting, setIsSubmitting] = useState(false)
	const [fileUploads, setFileUploads] = useState([])
	const [photoAttaches, setPhotoAttaches] = useState(initOrder?.order?.photoAttaches || [])
	const imagePreviews = useMemo(() => {
		return fileUploads.map((file) => URL.createObjectURL(file))
	}, [fileUploads])

	useEffect(() => {
		dispatch(fetchStores())
	}, [dispatch])

	const lineItems = useMemo(() => {
		if (initOrder?.order) {
			return initOrder.order.line_items
		}
		return [
			{ id: 8, name: 'Chè dừa Vani', quantity: 0, weight: 0 },
			{ id: 9, name: 'Chè dừa Café', quantity: 0, weight: 0 },
			{ id: 10, name: 'Bánh giòn 100g', quantity: 0, weight: 0 },
			{ id: 16, name: 'Chả giò', quantity: 0, weight: 0 },
		]
	}, [initOrder])

	useEffect(() => {
		if (initOrder?.order) {
			setOrderData({
				vani: initOrder.order.line_items?.find((item) => item.id === 8)?.quantity || 0,
				cafe: initOrder.order.line_items?.find((item) => item.id === 9)?.quantity || 0,
				banhGion: initOrder.order.line_items?.find((item) => item.id === 10)?.quantity || 0,
				chaGio: initOrder.order.line_items?.find((item) => item.id === 16)?.quantity || 0,
				freshQuantity: initOrder.order.freshQuantity ?? 0,
				cookedQuantity: initOrder.order.cookedQuantity ?? 0,
				totalPrice: formatPrice(initOrder.order.totalPrice ?? 0) || 0,
				phiGiaoHang: formatPrice(initOrder.order.shippingTotal ?? 0) || 0,
				thongTinDonHang: initOrder.order.detail || '',
				selectedStore: initOrder.toStore?._id || '',
				ghiChu: initOrder.order.note || '',
			})
			setPhotoAttaches(initOrder.order.photoAttaches || [])
		} else {
			setOrderData(initValues)
			setPhotoAttaches([])
		}
	}, [initOrder])

	//------HANDLE CHANGE FORM VALUE------
	const handleChangeTotalPrice = (e) => {
		let { name, value } = e.target
		let formatedNumber = formatPrice(value)
		let newValue = {
			...orderData,
			[name]: formatedNumber,
		}
		let newError = { ...orderData.errors }

		if (value.trim() === '') {
			newError[name] = '* Tiền không được để trống!'
		} else {
			if (Number(value) <= 0) {
				newError[name] = '* Tiền phải lớn hơn 0!'
			} else {
				newError[name] = ''
			}
		}
		setOrderData({ ...newValue, errors: newError })
	}

	const handleChange = (e) => {
		let { name, value } = e.target
		let newValue = { ...orderData, [name]: value }
		let newError = { ...orderData.errors }

		if (name === 'detail') {
			if (value.trim() === '') {
				newError[name] = '* Thông tin không được để trống!'
			} else {
				if (value.length < 5) {
					newError[name] = '* Thông tin tối thiểu 5 ký tự!'
				} else {
					newError[name] = ''
				}
			}
		}

		setOrderData({ ...newValue, errors: newError })
	}

	//---SOCKET START POST CREATE ORDER------
	const handleSubmit = async (orderType) => {
		setIsSubmitting(true)

		const newLineItems = []
		for (const item of lineItems) {
			let quantity = Number(item.quantity)
			if (item.id === 8) quantity = Number(orderData.vani) || 0
			if (item.id === 9) quantity = Number(orderData.cafe) || 0
			if (item.id === 10) quantity = Number(orderData.banhGion) || 0
			if (item.id === 16) quantity = Number(orderData.chaGio) || 0
			newLineItems.push({ ...item, quantity })
		}

		const totalPrice = orderData.totalPrice.replace(/,/g, '')
		const orderPayload = {
			freshQuantity: +(orderData.freshQuantity ?? '0'),
			cookedQuantity: +(orderData.cookedQuantity ?? '0'),
			totalPrice: +totalPrice,
			detail: orderData.thongTinDonHang,
			payment: 'cash',
			line_items: newLineItems,
			note: orderData.ghiChu || '',
		}

		if (orderData.phiGiaoHang) {
			orderPayload.shippingTotal = +orderData.phiGiaoHang.replace(/,/g, '')
		}

		try {
			const formData = new FormData()
			fileUploads.forEach((file) => {
				formData.append('photos', file)
			})
			formData.append('order', JSON.stringify(orderPayload))
			formData.append('toStore', orderData.selectedStore)

			if (initOrder) {
				formData.append('orderId', initOrder._id)
				formData.append('existingPhotos', JSON.stringify(photoAttaches))
				await apiBase.put(api.PUT_UPDATE_INTERNAL_SALE_ORDER, formData)
				enqueueSnackbar('Đơn hàng đã được cập nhật thành công!', { variant: 'success' })
			} else {
				if (orderType) formData.append('orderType', orderType)
				await apiBase.post(api.POST_CREATE_INTERNAL_SALE_ORDER, formData)
				enqueueSnackbar('Đơn hàng đã được tạo thành công!', { variant: 'success' })
			}
		} catch (error) {
			enqueueSnackbar(error?.response?.data?.message, { variant: 'error' })
		}

		setOrderData(initValues)
		closeForm(true)
		setIsSubmitting(false)
	}

	const onFileChange = async (e) => {
		const files = Array.from(e.target.files)
		const compressedFiles = await Promise.all(files.map(compressImage))
		setFileUploads((prev) => [...prev, ...compressedFiles])
	}

	const removeFile = (index, type) => {
		if (type === 'new') {
			setFileUploads((prev) => prev.filter((_, i) => i !== index))
		} else {
			setPhotoAttaches((prev) => prev.filter((_, i) => i !== index))
		}
	}

	return (
		<Modal open={open} onClose={() => closeForm()} sx={styles.modal}>
			<Box
				onClick={(e) => {
					e.stopPropagation()
				}}
				sx={styles.box}>
				<Box component='form' onSubmit={handleSubmit} noValidate sx={styles.form}>
					<Box
						sx={{
							display: 'grid',
							gridTemplateColumns: 'repeat(2, 1fr)',
							gap: 2,
							mb: 2,
						}}>
						<CustomInput
							placeholder='Vani (bịch)'
							value={orderData.vani}
							onChange={(e) => handleChange({ target: { name: 'vani', value: e.target.value } })}
						/>
						<CustomInput
							placeholder='Cafe (bịch)'
							value={orderData.cafe}
							onChange={(e) => handleChange({ target: { name: 'cafe', value: e.target.value } })}
						/>
						<CustomInput
							placeholder='Chả Giò (phần)'
							value={orderData.chaGio}
							onChange={(e) => handleChange({ target: { name: 'chaGio', value: e.target.value } })}
						/>
						<CustomInput
							placeholder='Bánh Giòn (phần)'
							value={orderData.banhGion}
							onChange={(e) => handleChange({ target: { name: 'banhGion', value: e.target.value } })}
						/>
					</Box>

					{/* Khối lượng nem */}
					<Box
						sx={{
							display: 'grid',
							gridTemplateColumns: 'repeat(2, 1fr)',
							gap: 2,
							mb: 2,
						}}>
						<CustomInput
							placeholder='Nem sống (kg)'
							value={orderData.freshQuantity}
							onChange={(e) => handleChange({ target: { name: 'freshQuantity', value: e.target.value } })}
						/>
						<CustomInput
							placeholder='Nem chín (kg)'
							value={orderData.cookedQuantity}
							onChange={(e) => handleChange({ target: { name: 'cookedQuantity', value: e.target.value } })}
						/>
					</Box>

					{/* Tổng tiền và Phí giao hàng */}
					<Box
						sx={{
							display: 'grid',
							gridTemplateColumns: 'repeat(2, 1fr)',
							gap: 2,
							mb: 2,
						}}>
						<CustomInput
							placeholder='Tổng tiền'
							value={orderData.totalPrice}
							onChange={(e) => handleChangeTotalPrice({ target: { name: 'totalPrice', value: e.target.value } })}
						/>
						<CustomInput
							placeholder='Phí giao hàng'
							value={orderData.phiGiaoHang}
							onChange={(e) => handleChangeTotalPrice({ target: { name: 'phiGiaoHang', value: e.target.value } })}
						/>
					</Box>

					{/* Multiline Fields */}
					<Box sx={{ mb: 2 }}>
						<CustomInput
							placeholder='Thông tin đơn hàng'
							value={orderData.thongTinDonHang}
							onChange={(e) => handleChange({ target: { name: 'thongTinDonHang', value: e.target.value } })}
							multiline
							rows={2}
						/>
					</Box>

					<Box sx={{ mb: 2 }}>
						<FormControl fullWidth>
							<Select
								value={orderData.selectedStore}
								onChange={(e) => handleChange({ target: { name: 'selectedStore', value: e.target.value } })}
								displayEmpty
								sx={{
									height: '44px',
									borderRadius: '16px',
									'& .MuiSelect-select': {
										padding: '8px 14px',
									},
								}}>
								<MenuItem value='' disabled>
									Chọn cửa hàng
								</MenuItem>
								{storesFromRedux?.map((store) => (
									<MenuItem key={store._id} value={store._id}>
										{store.name}
									</MenuItem>
								))}
							</Select>
						</FormControl>
					</Box>

					<Box sx={{ mb: 2 }}>
						<CustomInput
							placeholder='Ghi chú (nếu có)'
							value={orderData.ghiChu}
							onChange={(e) => handleChange({ target: { name: 'ghiChu', value: e.target.value } })}
							multiline
							rows={1}
						/>
					</Box>

					<Box
						sx={{ display: 'flex', flexDirection: 'row', mx: 1, gap: 2, flexWrap: 'wrap', alignItems: 'flex-start' }}>
						<Box
							sx={{
								...styles.timerIcons,
								color: mainGreen,
							}}>
							<label htmlFor='choose-photo' style={styles.label}>
								<CameraAltIcon sx={styles.cameraIcon} />
							</label>
							<input
								accept='image/*'
								onChange={onFileChange}
								id='choose-photo'
								type='file'
								style={styles.input}
								multiple
							/>
						</Box>

						<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, flex: 1 }}>
							{photoAttaches.map((photo, index) => (
								<Box key={`existing-${index}`} sx={styles.imageContainer}>
									<img loading='lazy' style={styles.previewImage} src={photo} alt={`existing-${index}`} />
									<Button variant='contained' onClick={() => removeFile(index, 'old')} sx={styles.removeImageButton}>
										×
									</Button>
								</Box>
							))}

							{imagePreviews.map((preview, index) => (
								<Box key={`new-${index}`} sx={styles.imageContainer}>
									<img loading='lazy' style={styles.previewImage} src={preview} alt={`new-${index}`} />
									<Button variant='contained' onClick={() => removeFile(index, 'new')} sx={styles.removeImageButton}>
										×
									</Button>
								</Box>
							))}

							{(imagePreviews.length > 0 || photoAttaches.length > 0) && (
								<Button
									variant='text'
									onClick={() => {
										setFileUploads([])
										setPhotoAttaches([])
									}}
									sx={{ ...styles.deleteButton, height: 'fit-content' }}>
									Xóa tất cả
								</Button>
							)}
						</Box>
					</Box>

					{/* Submit Button */}
					{initOrder ? (
						<Button
							variant='contained'
							fullWidth
							disabled={isSubmitting}
							sx={styles.submitButton}
							onClick={() => handleSubmit(initOrder.orderType)}>
							{isSubmitting ? 'Đang xử lý...' : 'Cập nhật đơn hàng'}
						</Button>
					) : (
						<Box sx={{ display: 'flex', gap: 2 }}>
							<Button
								variant='contained'
								fullWidth
								disabled={isSubmitting}
								sx={styles.submitButton}
								onClick={() => handleSubmit('SELL')}>
								{isSubmitting ? 'Đang xử lý...' : 'Tạo đơn bán'}
							</Button>
							<Button
								variant='contained'
								fullWidth
								disabled={isSubmitting}
								sx={styles.submitButton}
								style={{ backgroundColor: mainRed }}
								onClick={() => handleSubmit('BUY')}>
								{isSubmitting ? 'Đang xử lý...' : 'Tạo đơn mua'}
							</Button>
						</Box>
					)}
				</Box>
			</Box>
		</Modal>
	)
}

const styles = {
	modal: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
	},
	box: {
		background: mainGrey,
		border: `2px solid ${mainGreen}`,
		borderRadius: '1rem',
		boxShadow: 'rgba(9, 30, 66, 0.25) 0px 4px 8px -2px, rgba(9, 30, 66, 0.08) 0px 0px 0px 1px',
		maxWidth: '100%',
		width: '800px',
		maxHeight: '80vh',
		overflowY: 'auto',
		position: 'relative',
	},
	form: {
		overflowY: 'auto',
		pt: 2,
		pb: 8,
		position: 'relative',
		p: 2,
	},
	submitButton: {
		bgcolor: mainGreen,
		textTransform: 'none',
		borderRadius: '30px',
		py: 1.5,
		fontSize: '16px',
		'&:hover': {
			bgcolor: '#008d4c',
		},
		mt: 2,
		mb: 2,
	},
	label: {
		display: 'flex',
		cursor: 'pointer',
	},
	input: {
		display: 'none',
	},
	deleteButton: {
		color: mainRed,
		'&:hover': {
			backgroundColor: 'rgba(211, 47, 47, 0.04)',
		},
	},
	imageContainer: {
		position: 'relative',
		display: 'inline-block',
	},
	previewImage: {
		width: '60px',
		height: '60px',
		objectFit: 'cover',
		borderRadius: '8px',
	},
	removeImageButton: {
		position: 'absolute',
		top: -10,
		right: -10,
		minWidth: '22px',
		width: '22px',
		height: '22px',
		padding: 0,
		borderRadius: '50%',
		backgroundColor: mainRed,
		color: 'white',
		'&:hover': {
			backgroundColor: '#d32f2f',
		},
	},
}

